import type { LegacyRef, Ref } from 'react';
import type { Message, Participant, SessionInfo } from './types';
import { getPersonaColor } from './types';

export function SessionTranscriptMessageList({
  messages,
  sessionInfo,
  participants,
  user,
  loading,
  messagesEndRef,
}: {
  messages: Message[];
  sessionInfo: SessionInfo;
  participants: Participant[];
  user: { id?: string } | null;
  loading?: boolean;
  messagesEndRef?: Ref<HTMLDivElement | null>;
}) {
  const isMultiPersona = (sessionInfo.personas?.length ?? 0) > 1;
  const isMultiParticipant = participants.length > 1;

  return (
    <>
      {messages.map((msg) => {
        const personaIndex =
          msg.personaId && sessionInfo.personas
            ? sessionInfo.personas.findIndex((p) => p.id === msg.personaId)
            : 0;
        const color = getPersonaColor(Math.max(0, personaIndex));

        const showUserName =
          msg.role === 'user' && isMultiParticipant && msg.userName && msg.userId !== user?.id;

        return (
          <div key={msg.id}>
            {msg.role === 'assistant' && msg.personaName && isMultiPersona && (
              <div className={`text-xs font-medium mb-1 ${color.label}`}>{msg.personaName}</div>
            )}
            {showUserName && (
              <div className="text-xs font-medium mb-1 text-right text-green-700">
                {msg.userName}
              </div>
            )}
            <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] sm:max-w-[70%] px-3 sm:px-4 py-2 rounded-lg ${
                  msg.role === 'user'
                    ? 'bg-green-700 text-white'
                    : isMultiPersona
                      ? `${color.bg} ${color.text}`
                      : 'bg-gray-100 text-gray-900'
                }`}
              >
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
            {msg.hint && (
              <div className="flex justify-start mt-1">
                <div className="max-w-[70%] px-3 py-1.5 rounded-md bg-blue-50 border border-blue-100 text-blue-800 text-xs">
                  <span className="font-medium">Coaching tip:</span> {msg.hint}
                </div>
              </div>
            )}
          </div>
        );
      })}
      {loading && (
        <div className="flex justify-start">
          <div className="bg-gray-100 px-4 py-2 rounded-lg text-gray-400">Typing...</div>
        </div>
      )}
      {messagesEndRef ? <div ref={messagesEndRef as LegacyRef<HTMLDivElement>} /> : null}
    </>
  );
}
